import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { useQuery } from 'react-query';

import { ACTIVE_PROMO_ID_COOKIE, COUPON_COOKIE } from '~/features/cookies/constants';
import { getCookieDomain } from '~/features/cookies/utils/get-cookie-domain';
import { usePricingExperimentVariant } from '~/features/experiments/topaz/hooks';
import { useFreeTrialState } from '~/features/free-trial/hooks/use-free-trial-state';
import { useClientRender } from '~/hooks/use-client-render';

import { getPromotion } from '../controllers/get-promotion';
import { singleQueryParamValueSchema } from '../models/promotions';
import { isPersonalizedDiscountAllowed } from '../utils/is-personalized-discount-allowed';

export function usePromotion() {
  const { asPath, query, isReady } = useRouter();
  const promo = singleQueryParamValueSchema.parse(query.promo);
  const promoParticipation = singleQueryParamValueSchema.parse(query.promo_participation);
  const forcePromo = singleQueryParamValueSchema.parse(query.force_promo);
  const pathname = asPath?.replace(/(\?.+$|#.+$)/, '');
  const freeTrial = useFreeTrialState();
  const isClient = useClientRender();
  const isFreeTrialEnabled = freeTrial.status === 'on';
  const cookiedCoupon = isClient ? Cookies.get(COUPON_COOKIE) : undefined;
  const couponFromQueryParam = query?.coupon as string | undefined;
  const { variant: pricingVariant, isLoading: isPricingVariantLoading } = usePricingExperimentVariant();

  return useQuery(
    ['promotions', pathname, promo, promoParticipation, forcePromo, isFreeTrialEnabled, pricingVariant],
    async () => {
      const promotion = await getPromotion({
        pathname,
        promo,
        promoParticipation,
        forcePromo,
        isFreeTrialEnabled,
      });

      if (!promotion) {
        return null;
      }

      if (!isPersonalizedDiscountAllowed(promotion.type, pricingVariant)) {
        return null;
      }

      if (promotion.promotionId) {
        Cookies.set(ACTIVE_PROMO_ID_COOKIE, promotion.promotionId);
      }

      if (!couponFromQueryParam && !cookiedCoupon && promotion.promoCode) {
        Cookies.set(COUPON_COOKIE, promotion.promoCode, { maxAge: '604800', domain: getCookieDomain() }); // 604800 seconds = 1 week
      }

      return promotion;
    },
    {
      enabled: isReady && freeTrial.status !== 'loading' && !isPricingVariantLoading,
    }
  );
}
